import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
  Divider,
} from '@mui/material';
import {
  PhoneAndroid as PhoneAndroidIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Controlla se si vuole accedere con la versione mobile
  const isMobileLogin = location.search.includes('mobile=true') || 
                        (location.state as any)?.mobile === true;

  // Leggi messaggio di successo dallo state della navigazione
  useEffect(() => {
    if (location.state && (location.state as any).message) {
      setSuccessMessage((location.state as any).message);
      // Rimuovi il messaggio dallo state per evitare di mostrarlo di nuovo
      window.history.replaceState({}, '');
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Esegui login e ottieni i dati dell'utente
      const response = await authAPI.login(username, password);
      await login(username, password);
      
      // Se deve cambiare password, reindirizza al cambio password
      if (response.user.must_change_password) {
        navigate('/force-password-change');
      } else {
        // Se si è scelto l'accesso mobile, reindirizza direttamente a mobile-presence
        if (isMobileLogin) {
          navigate('/mobile-presence');
        } else {
          // Passa sempre dalla root: sarà NavigateToDashboard a decidere
          // la home corretta in base a ruolo, impersonazione e scope admin.
          navigate('/');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore durante il login');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url(/calendar-5983133_1920.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(13, 71, 161, 0.7)', // Sfondo blu scuro con trasparenza
        }
      }}
    >
      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              padding: 4, 
              width: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.95)', // Sfondo bianco leggermente trasparente
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography component="h1" variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                Monitoraggio Presenze
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Accedi al sistema
              </Typography>

              {successMessage && (
                <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
                  {successMessage}
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ 
                    mt: 3, 
                    mb: 2,
                    backgroundColor: '#0d47a1',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Accedi'}
                </Button>
              </Box>
              
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/reset-password')}
                  sx={{ cursor: 'pointer' }}
                >
                  Password dimenticata?
                </Link>
              </Box>
              
              <Divider sx={{ my: 3 }}>oppure</Divider>
              
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PhoneAndroidIcon />}
                onClick={() => navigate('/login?mobile=true', { replace: true })}
                sx={{
                  mb: 2,
                  borderColor: '#0d47a1',
                  color: '#0d47a1',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(13, 71, 161, 0.04)',
                  }
                }}
              >
                Accedi con Smartphone
              </Button>
              
              {isMobileLogin && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Modalità mobile attiva: dopo il login sarai reindirizzato alla versione ottimizzata per smartphone.
                </Alert>
              )}
          </Box>
        </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
